import { useEffect, useRef, useState } from "react"
import { AlertTriangle, ArrowUpRight, Check, ListTodo, Loader2, MapPin, Package, Users, Video } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { JobElapsedTimer } from "@/components/jobs/job-elapsed-timer"
import { useAgentRunsStore } from "@/stores/agent-runs-store"
import { visitAgentSurface } from "@/lib/open-workspace-agent"
import { resolveHandoffChipState, TASKS_HANDOFF_FINISHED_EVENT } from "@/lib/agent-handoff"
import { cn } from "@/lib/utils"
import { useTranslation } from "@/hooks/use-translation"
import type { AgentHandoffChipMeta, AgentHandoffSurfaceName } from "@/lib/agent-api"

const STALE_TICK_MS = 30_000

const SURFACE_ICON: Record<AgentHandoffSurfaceName, LucideIcon> = {
  characters: Users,
  locations: MapPin,
  props: Package,
  shot: Video,
  tasks: ListTodo,
}

export function AgentHandoffChip({
  projectId,
  meta,
  startedAt,
}: {
  projectId: number
  meta: AgentHandoffChipMeta
  startedAt?: string
}) {
  const { t } = useTranslation("workspace")
  const run = useAgentRunsStore((s) => s.runs[meta.target_conversation_id] ?? null)
  const [now, setNow] = useState(() => Date.now())
  const state = resolveHandoffChipState(meta, run, now)
  // Часы — created_at этой плашки. run.started_at общий на диалог и обнуляется каждым claim_run!.
  const previousState = useRef<typeof state | null>(null)

  useEffect(() => {
    const previous = previousState.current
    previousState.current = state
    if (previous == null || meta.surface !== "tasks") return
    if (previous === "running" && (state === "done" || state === "failed")) {
      window.dispatchEvent(new CustomEvent(TASKS_HANDOFF_FINISHED_EVENT))
    }
  }, [meta.surface, state])

  // Прогон мог оборваться вместе с воркером: статус в БД так и остался processing,
  // поэтому порог «протухания» пересчитываем сами, не дожидаясь события.
  useEffect(() => {
    if (state !== "running") return
    const id = window.setInterval(() => setNow(Date.now()), STALE_TICK_MS)
    return () => window.clearInterval(id)
  }, [state])

  const Icon = SURFACE_ICON[meta.surface]
  const surfaceLabel = t(`agent.handoff.surface.${meta.surface}`)
  const statusLabel = t(`agent.handoff.status.${state}`)
  const tooltip = meta.task
    ? t("agent.handoff.tooltipWithTask", { surface: surfaceLabel, status: statusLabel, task: meta.task })
    : t("agent.handoff.tooltip", { surface: surfaceLabel, status: statusLabel })
  // У ассистента задач нет своего чата, плашка только показывает статус.
  const navigable = meta.surface !== "tasks"
  const chipClassName = cn(
    "flex w-fit items-center gap-1.5 rounded-md border border-ui-border bg-ui-surface-sunken/60 px-1.5 py-1 text-left text-[11px]",
    navigable && "cursor-pointer transition-colors hover:border-ui-accent/50 hover:bg-ui-surface-sunken",
  )
  const chipBody = (
    <>
      <Icon className="h-3 w-3 shrink-0 text-ui-text-secondary" />
      <span className="max-w-[12rem] truncate text-ui-text-primary">{surfaceLabel}</span>
      {state === "running" ? <Loader2 className="h-3 w-3 shrink-0 animate-spin text-ui-text-secondary" /> : null}
      {state === "running" && startedAt ? <JobElapsedTimer startedAt={startedAt} active /> : null}
      {state === "done" ? <Check className="h-3 w-3 shrink-0 text-[var(--ui-badge-emerald-fg)]" /> : null}
      {state === "failed" ? <AlertTriangle className="h-3 w-3 shrink-0 text-[var(--ui-badge-amber-fg)]" /> : null}
      {navigable ? <ArrowUpRight className="h-3 w-3 shrink-0 text-ui-text-muted" /> : null}
    </>
  )

  return (
    <ActionHintTooltip label={tooltip}>
      {navigable ? (
        <button
          type="button"
          onClick={() =>
            visitAgentSurface({
              projectId,
              surface: meta.surface,
              entityId: meta.entity_id ?? null,
              shotId: meta.shot_id ?? null,
            })
          }
          aria-label={tooltip}
          className={chipClassName}
        >
          {chipBody}
        </button>
      ) : (
        <div className={chipClassName}>{chipBody}</div>
      )}
    </ActionHintTooltip>
  )
}
